import { filter } from 'rxjs/operators';
import { Component, EventEmitter, Inject, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GenericResponse, Select } from '@interfaces';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { UtilService } from 'src/app/services/util.service';
import { SelectedTradingAccount } from '../../new-contract-large-accounts.model';
import { NewContractLargeAccountsService } from '../../new-contract-large-accounts.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-new-contract-select-values',
  templateUrl: './new-contract-select-values.component.html',
  styleUrls: ['./new-contract-select-values.component.scss']
})
export class NewContractSelectValuesComponent implements OnInit, OnDestroy {
  public valuesForm: FormGroup = new FormGroup({
    traddingAccount: new FormControl([], Validators.required),
    socialReason: new FormControl({ value: [], disabled: true }),
    installation: new FormControl({ value: [], disabled: true })
  });
  public traddingAccountList: Select[] = [];
  public socialReasonList: Select[] = [];
  public installationList: Select[] = [];
  private destroyed$: Subject<boolean> = new Subject<boolean>();

  @Input() codTradingAccount: string;
  @Output() canGoNext: EventEmitter<SelectedTradingAccount> = new EventEmitter();
  @Output() changeLoader: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private newContractLargeAccountsService: NewContractLargeAccountsService,
    private utilService: UtilService,
    @Inject(I18NEXT_SERVICE) private i18NextService: ITranslationService
  ) { }

  ngOnInit(): void {
    this.subscribeControls();
    this.getTraddingAccounts();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private subscribeControls(): void {
    this.valuesForm.get('traddingAccount').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.getSocialReason();
    });
    this.valuesForm.get('socialReason').valueChanges.pipe(
      filter(value => !!value),
      takeUntil(this.destroyed$)
    ).subscribe(value => {
      if (value.length !== 0 && value !== 'all') {
        this.valuesForm.get('installation').enable();
        this.valuesForm.get('installation').setValue([]);
      } else {
        this.valuesForm.get('installation').disable();
      }
      if (value.length !== 0) {
        this.getInstallations(value === 'all');
      }
      this.valuesForm.updateValueAndValidity();
    });
    this.valuesForm.statusChanges.pipe(
      filter(value => !!value),
      takeUntil(this.destroyed$)
    ).subscribe(value => {
      const data: SelectedTradingAccount = {
        ...{
          valid: value === 'VALID',
          allSocialReason: this.valuesForm.get('socialReason')?.value === 'all' || this.valuesForm.get('socialReason')?.value?.length === 0,
          allInstallation: this.valuesForm.get('installation')?.value === 'all' || this.valuesForm.get('installation')?.value?.length === 0,
          traddingAccountLabel: this.traddingAccountList?.find(value2 =>
            value2?.value === this.valuesForm.get('traddingAccount')?.value
          )?.label,
          socialReasonLabels: this.valuesForm.get('socialReason').value === 'all' ?
            this.socialReasonList.map(res => res.label) :
            [this.socialReasonList?.find(res => this.valuesForm.get('socialReason').value === res.value)?.label],
          installationLabels: this.valuesForm.get('installation').value === 'all' ?
            this.installationList.map(res => res.label) :
            [this.installationList?.find(res => this.valuesForm.get('installation').value === res.value)?.label],
        },
        ...this.valuesForm.getRawValue()
      };
      if (data.allSocialReason) {
        data.socialReason = this.socialReasonList.filter(res => res.value !== 'all').map(res2 => res2.value);
      } else {
        data.socialReason = typeof (data.socialReason) === 'string' ? [data.socialReason as unknown] as string[] : data.socialReason;
      }
      if (data.allInstallation) {
        data.installation = this.installationList.filter(res => res.value !== 'all').map(res2 => res2.value);
      } else {
        data.installation = typeof (data.installation) === 'string' ? [data.installation as unknown] as string[] : data.installation;
      }
      this.canGoNext.emit(data);
    });
  }

  private genericMapper(label: string, value: string | number, values: any = []): any[] {
    const aux = [];
    values.forEach(element => {
      aux.push({ label: element[label], value: element[value] });
    });
    return aux;
  }

  public getTraddingAccounts(event?): void {
    this.newContractLargeAccountsService.getTraddingAccounts({ Filter: event?.filter }).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        if (response.success) {
          this.traddingAccountList = this.genericMapper(
            'nombreCuentaNegociadora',
            'codCuentaNegociadora',
            response.data.values
          );
          if (this.codTradingAccount) {
            this.valuesForm.get('traddingAccount').setValue(this.codTradingAccount);
            this.valuesForm.get('traddingAccount').disable();
          }
        } else {
          this.utilService.toastErrors(response);
        }
      },
      error => {
        this.utilService.toastErrors(error);
      }
    );
  }

  public getSocialReason(event?): void {
    this.newContractLargeAccountsService.getSocialReason(
      { CodCuentaNegociadora: this.valuesForm.get('traddingAccount').value, Filter: event?.filter }
    ).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        if (response.success) {
          this.socialReasonList = this.genericMapper('nombreRazonSocial', 'codRazonSocial', response.data.values);
          if (this.socialReasonList.length > 1) {
            this.socialReasonList.unshift({ value: 'all', label: this.i18NextService.t('NEW_CONTRACT_LA.ALL_SOCIAL_REASONS') });
          }
          this.valuesForm.get('socialReason').setValue([]);
          this.valuesForm.get('socialReason').enable();
          this.valuesForm.updateValueAndValidity();
          this.installationList = [];
        } else {
          this.utilService.toastErrors(response);
        }
      },
      error => {
        this.utilService.toastErrors(error);
      }
    );
  }

  public getInstallations(all?: boolean, event?): void {
    this.newContractLargeAccountsService.getInstallations({
      CodCuentaNegociadora: this.valuesForm.get('traddingAccount').value,
      CodesRazonSocial: this.valuesForm.get('socialReason').value === 'all' ?
        this.socialReasonList.map(res => res.value).filter(res2 => res2 !== 'all') :
        this.valuesForm.get('socialReason').value,
      Filter: event?.filter
    }).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        if (response.success) {
          this.installationList = this.genericMapper('nombreInstalacion', 'codInstalacion', response.data.values);
          if (this.installationList.length > 1) {
            this.installationList.unshift({ value: 'all', label: this.i18NextService.t('NEW_CONTRACT_LA.ALL_INSTALLATIONS') });
            if (all) {
              this.valuesForm.get('installation').setValue('all');
            }
          }
        } else {
          this.utilService.toastErrors(response);
        }
      },
      error => {
        this.utilService.toastErrors(error);
      }
    );
  }
}
